import React, { useEffect, useState, useMemo } from 'react';

interface MediaRecord {
  id: string;
  mediaType: 'image' | 'video';
  usage: string[];
  category?: string;
  cloudinaryUrl: string;
  cloudinaryPublicId?: string;
  products: string[];
  usageCount: number;
  createdAt?: string;
}

interface ExtraMediaBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMedia: (mediaId: string, recordId: string, cloudinaryUrl: string) => void;
  currentProductCategory?: string;
  currentProductId?: string;
  usageFilter?: 'product-main' | 'product-secondary' | 'product-third' | 'product-extra' | 'all';
  title?: string;
  description?: string;
}

const USAGE_LABELS: Record<string, string> = {
  'product-main': 'Main',
  'product-secondary': 'Secondary',
  'product-third': 'Third',
  'product-extra': 'Extra',
  'category-hero': 'Category Hero',
  'site-static': 'Site Static',
};

export const ExtraMediaBrowser: React.FC<ExtraMediaBrowserProps> = ({
  isOpen,
  onClose,
  onSelectMedia,
  currentProductCategory,
  currentProductId,
  usageFilter: initialUsageFilter = 'all',
  title = 'Media Library',
  description = 'Select media to add to this product',
}) => {
  const [media, setMedia] = useState<MediaRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterUsage, setFilterUsage] = useState<string>(initialUsageFilter);
  const [filterMediaType, setFilterMediaType] = useState<'all' | 'image' | 'video'>('all');
  const [sortBy, setSortBy] = useState<'default' | 'newest' | 'oldest'>('default');

  useEffect(() => {
    if (isOpen) {
      fetchAllMedia();
      document.body.style.overflow = 'hidden';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const fetchAllMedia = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('cms_token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Always fetch ALL media - we filter in the UI
      const response = await fetch('/api/cms/media', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || 'Failed to fetch media');
      }

      setMedia(data.media || []);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch media';
      setError(errorMsg);
      console.error('Media fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Deduplicate media by Cloudinary URL (keep first occurrence, aggregate usage tags)
  const deduplicatedMedia = useMemo(() => {
    const urlMap = new Map<string, MediaRecord>();

    media.forEach((m) => {
      const existing = urlMap.get(m.cloudinaryUrl);
      if (existing) {
        // Merge usage tags from duplicate records
        const mergedUsage = new Set([...(existing.usage || []), ...(m.usage || [])]);
        existing.usage = Array.from(mergedUsage);
        // Sum up usage counts
        existing.usageCount = (existing.usageCount || 0) + (m.usageCount || 0);
      } else {
        // First occurrence - clone to avoid mutating original
        urlMap.set(m.cloudinaryUrl, { ...m, usage: [...(m.usage || [])] });
      }
    });

    return Array.from(urlMap.values());
  }, [media]);

  // Get unique categories from deduplicated media
  const categories = useMemo(() => {
    const cats = new Set<string>();
    deduplicatedMedia.forEach((m) => {
      if (m.category) cats.add(m.category);
    });
    return Array.from(cats).sort();
  }, [deduplicatedMedia]);

  // Get unique usage types from deduplicated media
  const usageTypes = useMemo(() => {
    const types = new Set<string>();
    deduplicatedMedia.forEach((m) => {
      if (Array.isArray(m.usage)) {
        m.usage.forEach((u) => types.add(u));
      }
    });
    return Array.from(types).sort();
  }, [deduplicatedMedia]);

  const filteredMedia = deduplicatedMedia.filter((m) => {
    // Filter by media type
    if (filterMediaType !== 'all' && m.mediaType !== filterMediaType) {
      return false;
    }

    // Filter by category
    if (filterCategory !== 'all' && m.category !== filterCategory) {
      return false;
    }

    // Filter by usage type
    if (filterUsage !== 'all') {
      if (!Array.isArray(m.usage) || !m.usage.includes(filterUsage)) {
        return false;
      }
    }

    // Loose search: split on spaces/dashes, all terms must match somewhere
    if (searchTerm) {
      const terms = searchTerm.toLowerCase().split(/[\s\-_]+/).filter(Boolean);
      const haystack = [
        m.id,
        m.category || '',
        m.mediaType,
        ...(m.usage || []),
      ].join(' ').toLowerCase();

      return terms.every(term => haystack.includes(term));
    }

    return true;
  });

  // Apply sorting
  const sortedMedia = useMemo(() => {
    if (sortBy === 'default') return filteredMedia;
    return [...filteredMedia].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
    });
  }, [filteredMedia, sortBy]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {description}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Search by ID, category, usage..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Media Type Filter */}
            <div className="w-36">
              <select
                value={filterMediaType}
                onChange={(e) => setFilterMediaType(e.target.value as any)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Media</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
              </select>
            </div>

            {/* Usage Type Filter */}
            <div className="w-44">
              <select
                value={filterUsage}
                onChange={(e) => setFilterUsage(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Usage</option>
                {usageTypes.map((type) => (
                  <option key={type} value={type}>
                    {USAGE_LABELS[type] || type}
                  </option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div className="w-44">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort By */}
            <div className="w-44">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'default' | 'newest' | 'oldest')}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="default">Sort: Default</option>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>

            {/* Clear Filters */}
            {(filterUsage !== 'all' || filterMediaType !== 'all' || filterCategory !== 'all' || searchTerm || sortBy !== 'default') && (
              <button
                onClick={() => {
                  setFilterUsage('all');
                  setFilterMediaType('all');
                  setFilterCategory('all');
                  setSearchTerm('');
                  setSortBy('default');
                }}
                className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Clear filters
              </button>
            )}
          </div>

          {currentProductCategory && filterCategory !== currentProductCategory && (
            <button
              onClick={() => setFilterCategory(currentProductCategory)}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline"
            >
              Show only "{currentProductCategory}" category
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 dark:bg-gray-900">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Loading media...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {!loading && !error && sortedMedia.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <p className="text-lg mb-2">No extra media found</p>
              <p className="text-sm">
                {searchTerm || filterCategory !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Upload some extra media first'}
              </p>
            </div>
          )}

          {!loading && !error && sortedMedia.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {sortedMedia.map((mediaItem) => {
                const isLinked = currentProductId ? mediaItem.products.includes(currentProductId) : false;

                return (
                  <div
                    key={mediaItem.id}
                    className={`rounded-lg overflow-hidden transition-shadow bg-white dark:bg-gray-800 ${
                      isLinked
                        ? 'ring-2 ring-green-500 border border-green-300 dark:border-green-700 opacity-75'
                        : 'border border-gray-200 dark:border-gray-700 hover:shadow-lg dark:hover:shadow-gray-900/50'
                    }`}
                  >
                    {/* Preview */}
                    <div className="aspect-square bg-gray-100 dark:bg-gray-700 relative">
                      {mediaItem.mediaType === 'image' ? (
                        <img
                          src={mediaItem.cloudinaryUrl}
                          alt={mediaItem.id}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          src={mediaItem.cloudinaryUrl}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          preload="metadata"
                        />
                      )}
                      {isLinked && (
                        <div className="absolute top-2 left-2 bg-green-600 text-white text-[10px] font-medium px-2 py-0.5 rounded">
                          Linked
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-3 space-y-2">
                      <div className="text-xs font-mono text-gray-600 dark:text-gray-400 truncate" title={mediaItem.cloudinaryPublicId || mediaItem.id}>
                        {(mediaItem.cloudinaryPublicId || mediaItem.id).split('/').pop()}
                      </div>

                      {/* Usage Tags */}
                      {mediaItem.usage && mediaItem.usage.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {mediaItem.usage.map((u) => (
                            <span
                              key={u}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                u === 'product-main'
                                  ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400'
                                  : u === 'product-secondary'
                                  ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400'
                                  : u === 'product-third'
                                  ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400'
                                  : u === 'product-extra'
                                  ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-400'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                              }`}
                            >
                              {USAGE_LABELS[u] || u}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{mediaItem.mediaType}</span>
                        <span>{mediaItem.usageCount} product{mediaItem.usageCount !== 1 ? 's' : ''}</span>
                      </div>

                      {mediaItem.category && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {mediaItem.category}
                        </div>
                      )}

                      {isLinked ? (
                        <div className="w-full text-center text-sm font-medium py-2 px-4 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                          Already linked
                        </div>
                      ) : (
                        <button
                          onClick={() => onSelectMedia(mediaItem.id, mediaItem.id, mediaItem.cloudinaryUrl)}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded transition-colors"
                        >
                          Add to Product
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {sortedMedia.length} media item{sortedMedia.length !== 1 ? 's' : ''} shown
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
